import Layout from 'components/layout';
import BlogPage, { BLOG_PAGE_HERO_TYPE, BlogPageProps } from 'components/blog-page/blog-page';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { BLOG_POSTS_PAGE_SIZE, FIRST_PAGE_POSTS_COUNT, getBlogArticlesByOffset } from 'data/blog-article';
import { CommercialBlogPostsApi } from 'data/wordpress/utils';
import { isTenantExpCommercial, isTenantExpCommercialCa, isTenantExpCA, isTenantExpUS, isZoocasaTenant } from '@tenants';
import { NOT_FOUND } from 'constants/not-found';
import { createGetServerSideProps } from 'utils/next/shared-getServerSideProps';
import { getSiteLocationFromRequest } from 'utils/site-location';
import { getUserLocationFromServerSideRequest } from 'utils/user-location';
import { getCityStats, getTopCities } from 'data/insights/fetcher';
import { standardizedCityName } from 'utils/standardized-city-name';
import MarketFaqPage from 'components/pages/market-faq/market-faq-page';
import styles from './style.module.scss';

import type { HandlerType } from 'utils/next/shared-getServerSideProps';
import type { GetServerSidePropsContext } from 'next';
import type { FeaturesType } from 'contexts';
import type { TenantTypes } from '@tenants';
import type { CountryCode } from 'types/countries';
import type { ProvinceOrStateCode } from 'utils/province_or_state';
import type { CityStats, TopCities } from 'data/insights/types';
import HeadData from 'components/head-data';
import FormPage from 'components/form-page';
import HubspotForm from 'components/hubspot-form';

const DEFAULT_PAGE_NUMBER = 1;
export const INSIGHTS_BLOG_PATH = '/insights';
export const COMMERCIAL_LIFE_CATEGORY_ID = 7;
const MARKET_FAQ_GRID_SIZE = 16;

// hubspot form ids
export const FORM_ID = '8233db48-a971-4020-823c-cb74a1630e6b';
export const PORTAL_ID = '24210079';
export const HUBSPOT_FORM_SRC = 'https://js.hsforms.net/forms/embed/24210079.js';

export type InsightsPageProps =
  | ({ kind: 'blog' } & BlogPageProps)
  | { kind: 'market-faq'; countryCode: CountryCode; topCities: TopCities[]; statsBySlug: Record<string, CityStats> };

/**
 * Groups the top-cities list by province (provinces ranked by total combined
 * popularity), then ranks cities within each province by listing count —
 * mirrors the grouping in the design mock (all Ontario markets together,
 * then the next province, etc.) rather than one flat popularity ranking.
 */
function sortByProvinceThenPopularity(cities: TopCities[]): TopCities[] {
  const provinceTotals = new Map<string, number>();
  cities.forEach(city => provinceTotals.set(city.Province, (provinceTotals.get(city.Province) ?? 0) + city.TotalCount));

  return [...cities].sort((a, b) => {
    if (a.Province !== b.Province) {
      return (provinceTotals.get(b.Province) ?? 0) - (provinceTotals.get(a.Province) ?? 0);
    }
    return b.TotalCount - a.TotalCount;
  });
}

const handler: HandlerType<InsightsPageProps> = async (context: GetServerSidePropsContext, features: FeaturesType, tenant: TenantTypes) => {

  if (isZoocasaTenant(tenant.id) || isTenantExpCA(tenant.id) || isTenantExpUS(tenant.id)) {
    const userLocation = await getUserLocationFromServerSideRequest(context.req);
    const countryCode = getSiteLocationFromRequest(context.req, userLocation?.countryCode);

    const rawTopCities = (await getTopCities(countryCode, undefined, undefined, MARKET_FAQ_GRID_SIZE)) ?? [];
    const topCities = sortByProvinceThenPopularity(
      rawTopCities.map(city => ({
        ...city,
        Name: standardizedCityName(city.Name),
        // The country-wide /insights/popular query returns an empty Province — derive it
        // from the slug's suffix instead, same convention as use-popular-cities.ts.
        Province: city.Province || city.Slug.slice(-2).toUpperCase(),
      }))
    );

    const statsResults = await Promise.allSettled(
      topCities.map(city => getCityStats(countryCode, city.Province.toLowerCase() as ProvinceOrStateCode, city.Name))
    );
    const statsBySlug: Record<string, CityStats> = {};
    statsResults.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        statsBySlug[topCities[index].Slug] = result.value;
      }
    });

    return { props: { kind: 'market-faq', countryCode, topCities, statsBySlug }};
  }

  if (!isTenantExpCommercial(tenant.id, features) && !isTenantExpCommercialCa(tenant.id, features)) {
    return NOT_FOUND;
  }

  const pageNumber = parseInt(context?.query?.page as string) || DEFAULT_PAGE_NUMBER;
  const isFirstPage = pageNumber === 1;
  const postsPerPage = isFirstPage ? FIRST_PAGE_POSTS_COUNT : BLOG_POSTS_PAGE_SIZE;
  const offset = isFirstPage ? 0 : FIRST_PAGE_POSTS_COUNT + ((pageNumber - 2) * BLOG_POSTS_PAGE_SIZE);
  const agentCopy = isTenantExpCommercial(tenant.id, features) ? 'advisors' : 'agents';
  const insightsDescription = `We're a cloud-based brokerage that's powered by top ${agentCopy} and <span>cutting-edge technology</span>. Read more about our latest news and innovations here.`;

  try {
    const response = await getBlogArticlesByOffset({
      limit: postsPerPage,
      offset,
      excludeCategories: [COMMERCIAL_LIFE_CATEGORY_ID], // We don't want to show commercial life posts on the insights page,
      wpApi: CommercialBlogPostsApi,
    });

    const pagesCount = Math.max(1, response.pagination.totalPages);

    const processedPosts = response.articles.map((article, index) => ({
      id: article.id,
      date: article.date,
      link: `${INSIGHTS_BLOG_PATH}${new URL(article.link).pathname}`,
      title: article.title,
      excerpt: article.excerpt || '',
      isFeatured: index === 0 && isFirstPage,
      shouldHideExcerpt: index < 3 && isFirstPage,
      thumbnail: article.thumbnail,
    }));

    return {
      props: {
        kind: 'blog',
        posts: processedPosts,
        pageNumber,
        pagesCount,
        heroProps: {
          type: BLOG_PAGE_HERO_TYPE.IMAGE_IN_TEXT,
          props: {
            backgroundImageSrc: '/next/assets/images/exp-commercial/commercial-hero-poster.webp',
            title: 'Insights',
            description: insightsDescription,
          },
        },
      },
    };
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return {
      props: {
        kind: 'blog',
        posts: [],
        pageNumber,
        pagesCount: 1,
        heroProps: {
          type: BLOG_PAGE_HERO_TYPE.IMAGE_IN_TEXT,
          props: {
            backgroundImageSrc: '/next/assets/images/exp-commercial/commercial-hero-poster.webp',
            title: 'Insights',
            description: insightsDescription,
          },
        },
      },
    };
  }
};


export const getServerSideProps = createGetServerSideProps(handler);

export default function InsightsPage(props: InsightsPageProps) {
  const { push } = useRouter();
  const goToPage = useCallback(
    (page: number) => push((page === 1) ? INSIGHTS_BLOG_PATH : `${INSIGHTS_BLOG_PATH}?page=${page}`),
    [push]
  );

  if (props.kind === 'market-faq') {
    return <MarketFaqPage countryCode={props.countryCode} topCities={props.topCities} statsBySlug={props.statsBySlug} />;
  }

  return (
    <Layout showMobileBottomNav={false}>
      <HeadData data={{ title: 'Insights - eXp Commercial' }} />
      <BlogPage {...props} goToPage={goToPage}/>
      <FormPage
        className={styles['container']}
        formContainerClassname={styles['form-container']}
        title="Stay Informed"
        description="Get the latest commercial insights and success stories delivered to your inbox."
        formContent={<HubspotForm className="hs-form-frame" src={HUBSPOT_FORM_SRC} formId={FORM_ID} portalId={PORTAL_ID} region="na1" />}
      />
    </Layout>
  );
}
